import { Settings } from 'lucide-react';
import { createSearchParams, useNavigate } from 'react-router';
import { Fragment } from 'react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  activationNotificationOptionsRichDictionary,
  assetVisibilityOptionsRichDictionary,
} from 'src/containers/SoftwareEscrow/Technical';
import { Table, TableBody, TableCell, TableRow, TableFooter } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { storageRegionDictionary } from 'src/shared/entities';
import { AgreementFormEnum, EscrowPartyTypeEnum } from 'src/graphql/schema';
import { cn } from 'src/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  gracePeriodOptionsDictionary,
  disruptedNotificationsOptionsRichDictionary,
} from 'src/shared/entities/escrow-notification-options';
import { getEscrowActivityNotificationOptions } from 'src/shared/entities/escrow-activity-notification-options';
import { assetVersionsLimitOptionsDictionary } from 'src/shared/entities/asset-versions-limit-options';

import NeedChangesBanner from './NeedChangesBanner';

const { activityNotificationOptionsRichDictionary } = getEscrowActivityNotificationOptions('software');

const PartyNotifications = ({ onEdit, title }: { onEdit: (slug: string) => void; title?: string }) => {
  const {
    softwareEscrow: {
      currentCompanyRole,
      [`${currentCompanyRole!}ActivityNotification` as const]: activityNotification,
      [`${currentCompanyRole!}DisruptionNotification` as const]: disruptionNotification,
      [`${currentCompanyRole!}DisruptionNotificationGracePeriod` as const]: disruptionNotificationGracePeriod,
    },
  } = useSoftwareEscrow();
  const {
    icon: ActivityIcon,
    label: activityTitle,
    description: activityDescription,
  } = activityNotificationOptionsRichDictionary[activityNotification];
  const { icon: DisruptionIcon, label: disruptionTitle } =
    disruptedNotificationsOptionsRichDictionary[disruptionNotification];
  const gracePeriod = gracePeriodOptionsDictionary[disruptionNotificationGracePeriod ?? '']?.label;

  const handleEdit = () => onEdit('notification-settings');

  return (
    <div className="space-y-4">
      {title && <div className="text-base font-bold text-foreground">{title} settings</div>}
      <div className="rounded-sm border border-border overflow-hidden">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell className="w-[190px] font-semibold">Activity notifications</TableCell>
              <TableCell className="w-[85px]">:</TableCell>
              <TableCell>
                <div className="flex items-center gap-2 min-w-[250px]">
                  <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <ActivityIcon className="size-4 text-foreground" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{activityTitle}</div>
                    <div className="text-xs text-foreground/80">{activityDescription}</div>
                  </div>
                </div>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="w-[190px] font-semibold">Disrupted access notifications</TableCell>
              <TableCell className="w-[85px]">:</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <DisruptionIcon className="size-4 text-foreground" />
                  </div>
                  <div className="font-medium text-sm">
                    {disruptionTitle}
                    {gracePeriod && ` (${gracePeriod})`}
                  </div>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3}>
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={handleEdit}>
                    Edit
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

const StorageSettings = ({ onEdit }: { onEdit: (slug: string) => void }) => {
  const {
    softwareEscrow: { storageRegion, assetVersionsLimit },
  } = useSoftwareEscrow();

  const handleEdit = () => onEdit('storage-settings');

  const hasAddOn = storageRegion !== 'default' || !assetVersionsLimit;

  return (
    <div className="space-y-4">
      <div className="text-base font-bold text-foreground">Storage</div>
      <div className="rounded-sm border border-border overflow-hidden">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell className="w-[190px] font-semibold">Storage region</TableCell>
              <TableCell className="w-[85px]">:</TableCell>
              <TableCell className={cn(hasAddOn && 'w-[190px]')}>
                {storageRegionDictionary[storageRegion || '']}
              </TableCell>
              {storageRegion !== 'default' && (
                <TableCell>
                  <Badge>Add-on</Badge>
                </TableCell>
              )}
            </TableRow>
            <TableRow>
              <TableCell className="w-[190px] font-semibold">Version history</TableCell>
              <TableCell className="w-[85px]">:</TableCell>
              <TableCell className={cn(hasAddOn && 'w-[190px]')}>
                {assetVersionsLimitOptionsDictionary[assetVersionsLimit ?? 'full']}
              </TableCell>
              {!assetVersionsLimit && (
                <TableCell>
                  <Badge>Add-on</Badge>
                </TableCell>
              )}
            </TableRow>
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={hasAddOn ? 4 : 3}>
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={handleEdit}>
                    Edit
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

const InvitedPartyNotificationsCard = ({ onEdit }: { onEdit: (slug: string) => void }) => {
  const {
    softwareEscrow: { beneficiarySharedInformation },
  } = useSoftwareEscrow();

  const { icon: ActivityIcon, label: activityTitle } = activityNotificationOptionsRichDictionary.first;

  return (
    <Card
      className={cn(
        !beneficiarySharedInformation &&
          'bg-[linear-gradient(0deg,--alpha(var(--muted)/60%)_0%,--alpha(var(--muted)/60%)_100%)]',
      )}
    >
      <CardHeader className="relative">
        {!beneficiarySharedInformation && (
          <span className="absolute top-6 right-6 italic text-xs text-gray-400">Non-editable fields</span>
        )}
        <CardTitle className="flex items-center gap-2 text-xl">
          <Settings className="size-6" />
          Technical settings
        </CardTitle>
        <CardDescription>Notification settings</CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {!beneficiarySharedInformation ? (
          <Fragment>
            <NeedChangesBanner />
            <div className="rounded-sm border border-border overflow-hidden">
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell className="w-[190px] font-semibold">Activity notification</TableCell>
                    <TableCell className="w-[85px]">:</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-[250px]">
                        <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <ActivityIcon className="size-4 text-foreground" />
                        </div>
                        <div>
                          <div className="font-medium text-sm">{activityTitle}</div>
                          <div className="text-xs text-foreground/80">
                            Members will only receive an activation notification email once the escrow is activated.
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </Fragment>
        ) : (
          <PartyNotifications onEdit={onEdit} />
        )}
      </CardContent>
    </Card>
  );
};

const DepositorSettings = ({ onEdit }: { onEdit: (slug: string) => void }) => {
  const {
    softwareEscrow: {
      currentCompanyRole,
      depositorActivityNotification: activityNotification,
      depositorDisruptionNotification: disruptionNotification,
      depositorDisruptionNotificationGracePeriod: disruptionNotificationGracePeriod,
    },
  } = useSoftwareEscrow();
  const {
    icon: ActivityIcon,
    label: activityTitle,
    description: activityDescription,
  } = activityNotificationOptionsRichDictionary[activityNotification];
  const { icon: DisruptionIcon, label: disruptionTitle } =
    disruptedNotificationsOptionsRichDictionary[disruptionNotification];
  const gracePeriod = gracePeriodOptionsDictionary[disruptionNotificationGracePeriod ?? '']?.label;

  const handleEdit = () => onEdit('notification-settings');

  if (currentCompanyRole === EscrowPartyTypeEnum.Beneficiary) return null;

  return (
    <div className="space-y-4">
      <div className="text-base font-bold text-foreground">Depositor settings</div>
      <div className="rounded-sm border border-border overflow-hidden">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell className="w-[190px] font-semibold">Activity notifications</TableCell>
              <TableCell className="w-[85px]">:</TableCell>
              <TableCell>
                <div className="flex items-center gap-2 min-w-[250px]">
                  <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <ActivityIcon className="size-4 text-foreground" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{activityTitle}</div>
                    <div className="text-xs text-foreground/80">{activityDescription}</div>
                  </div>
                </div>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="w-[190px] font-semibold">Disrupted access notifications</TableCell>
              <TableCell className="w-[85px]">:</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <DisruptionIcon className="size-4 text-foreground" />
                  </div>
                  <div className="font-medium text-sm">
                    {disruptionTitle}
                    {gracePeriod && ` (${gracePeriod})`}
                  </div>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3}>
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={handleEdit}>
                    Edit
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

const BeneficiarySettingsTripartite = ({ onEdit }: { onEdit: (slug: string) => void }) => {
  const {
    softwareEscrow: {
      currentCompanyRole,
      beneficiarySharedInformation,
      beneficiaryActivityNotification: activityNotification,
      beneficiaryDisruptionNotification: disruptionNotification,
      beneficiaryDisruptionNotificationGracePeriod: disruptionNotificationGracePeriod,
    },
  } = useSoftwareEscrow();
  const {
    icon: ActivityIcon,
    label: activityTitle,
    description: activityDescription,
  } = activityNotificationOptionsRichDictionary[activityNotification];
  const { icon: DisruptionIcon, label: disruptionTitle } =
    disruptedNotificationsOptionsRichDictionary[disruptionNotification];
  const gracePeriod = gracePeriodOptionsDictionary[disruptionNotificationGracePeriod ?? '']?.label;
  const {
    icon: AssetVisibilityIcon,
    label: assetVisibilityTitle,
    description: assetVisibilityDescription,
  } = assetVisibilityOptionsRichDictionary[String(beneficiarySharedInformation)];

  const handleEdit = () => onEdit('asset-visibility');

  return (
    <div className="space-y-4">
      <div className="text-base font-bold text-foreground">Beneficiary settings</div>
      <div className="rounded-sm border border-border overflow-hidden">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell className="w-[190px] font-semibold">Asset visibility</TableCell>
              <TableCell className="w-[85px]">:</TableCell>
              <TableCell>
                <div className="flex items-center gap-2 min-w-[250px]">
                  <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <AssetVisibilityIcon className="size-4 text-foreground" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{assetVisibilityTitle}</div>
                    <div className="text-xs text-foreground/80">{assetVisibilityDescription}</div>
                  </div>
                </div>
              </TableCell>
            </TableRow>
            {currentCompanyRole === EscrowPartyTypeEnum.Beneficiary && beneficiarySharedInformation && (
              <Fragment>
                <TableRow>
                  <TableCell className="w-[190px] font-semibold">Activity notifications</TableCell>
                  <TableCell className="w-[85px]">:</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 min-w-[250px]">
                      <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <ActivityIcon className="size-4 text-foreground" />
                      </div>
                      <div>
                        <div className="font-medium text-sm">{activityTitle}</div>
                        <div className="text-xs text-foreground/80">{activityDescription}</div>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="w-[190px] font-semibold">Disrupted access notifications</TableCell>
                  <TableCell className="w-[85px]">:</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <DisruptionIcon className="size-4 text-foreground" />
                      </div>
                      <div className="font-medium text-sm">
                        {disruptionTitle}
                        {gracePeriod && ` (${gracePeriod})`}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </Fragment>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3}>
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={handleEdit}>
                    Edit
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

const BeneficiarySettingsBipartite = ({ onEdit }: { onEdit: (slug: string) => void }) => {
  const {
    softwareEscrow: { activationNotification },
  } = useSoftwareEscrow();

  const {
    icon: Icon,
    label,
    description,
  } = activationNotificationOptionsRichDictionary[String(activationNotification)];

  const handleEdit = () => onEdit('activation-notifications');

  return (
    <div className="space-y-4">
      <div className="text-base font-bold text-foreground">Beneficiary settings</div>
      <div className="rounded-sm border border-border overflow-hidden">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell className="w-[190px] font-semibold">Activation notification</TableCell>
              <TableCell className="w-[85px]">:</TableCell>
              <TableCell>
                <div className="flex items-center gap-2 min-w-[250px]">
                  <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <Icon className="size-4 text-foreground" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{label}</div>
                    <div className="text-xs text-foreground/80">{description}</div>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3}>
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={handleEdit}>
                    Edit
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

const TechnicalSettingsCard = () => {
  const navigate = useNavigate();
  const {
    softwareEscrow: {
      id,
      currentCompanyRole,
      startedBy,
      agreement: { agreementForm },
    },
  } = useSoftwareEscrow();

  const onEdit = (slug: string) =>
    navigate({
      pathname: `/software-escrows/onboarding/${id}/${slug}`,
      search: createSearchParams({
        isEdit: 'true',
      }).toString(),
    });

  const isInvitedParty = currentCompanyRole !== startedBy;

  if (isInvitedParty) return <InvitedPartyNotificationsCard onEdit={onEdit} />;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <Settings className="size-6" />
          Technical settings
        </CardTitle>
        <CardDescription>
          The types of notifications and information escrow members will receive, and the storage for escrowed material.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        <DepositorSettings onEdit={onEdit} />
        {agreementForm === AgreementFormEnum.Tripartite ? (
          <BeneficiarySettingsTripartite onEdit={onEdit} />
        ) : (
          <BeneficiarySettingsBipartite onEdit={onEdit} />
        )}
        <StorageSettings onEdit={onEdit} />
      </CardContent>
    </Card>
  );
};

export default TechnicalSettingsCard;
